import { formatNumber } from '@/helpers';
import { CampaignTabType } from '@/pages/campaigns/campaigns.page.utils';
import { useMaximizeStore } from '@/stores';
import { useNeedsAttentionStore } from '@/stores/campaigns/useNeedsAttentionStore';
import { InstalledCoMarketingTemplateWithCampaign, SponsoredCoMarketingTemplateWithCampaign } from '@/types';
import { Flex, Icon, Typography } from '@alpine-iq/ui-library';
import React from 'react';
import NeedsAttentionPage from '../pages/NeedsAttentionPage';

const EditRequestItem = React.lazy(() => import('./NeedsAttention/EditRequestItem'));
const SponsoredTemplateItem = React.lazy(() => import('./NeedsAttention/SponsoredTemplateItem'));
const InstalledTemplateItem = React.lazy(() => import('./NeedsAttention/InstalledtemplateItem'));

export const attentionCardStyle: React.CSSProperties = {
	borderRadius: 16,
	cursor: 'pointer',
	minWidth: 325,
	width: 325,
	maxWidth: 325,
	flexShrink: 0,
};

// Hook to calculate how many cards can fit in the container
function useResponsiveCardLimit() {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const [cardLimit, setCardLimit] = React.useState(4);

	React.useEffect(() => {
		const calculateCardLimit = () => {
			if (!containerRef.current) return;

			const containerWidth = containerRef.current.offsetWidth;
			const cardWidth = 325;
			const gapWidth = 16;

			// Calculate how many cards can fit: (containerWidth + gap) / (cardWidth + gap)
			// We add gap to containerWidth because the last card doesn't have a trailing gap
			const maxCards = Math.floor((containerWidth + gapWidth) / (cardWidth + gapWidth));

			// Ensure we show at least 1 card if there's enough space for it
			const newLimit = Math.max(1, maxCards);
			setCardLimit(newLimit);
		};

		calculateCardLimit();

		const resizeObserver = new ResizeObserver(calculateCardLimit);
		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	return { containerRef, cardLimit };
}

export default function CoMarketingNeedsAttention() {
	const { maximize } = useMaximizeStore();
	const { loaded: needsAttentionLoaded, combinedItems, initialize, combinedPagination } = useNeedsAttentionStore();
	const { containerRef, cardLimit } = useResponsiveCardLimit();

	React.useEffect(() => {
		if (!needsAttentionLoaded) {
			// Use light mode for card view - only fetch minimal data
			initialize('light');
		}
	}, [needsAttentionLoaded, initialize]);

	// For card view, show all items we have (they're already limited by light mode)
	const displayedItems = React.useMemo(() => {
		return combinedItems;
	}, [combinedItems]);

	// Render the combined items
	const renderedItems = React.useMemo(() => {
		return displayedItems.map((item) => {
			if (!('type' in item)) {
				return (
					<EditRequestItem
						key={item.id}
						editRequest={item}
					/>
				);
			}

			if (item.type === 'sponsored') {
				return (
					<SponsoredTemplateItem
						key={item.id}
						template={item as unknown as SponsoredCoMarketingTemplateWithCampaign}
					/>
				);
			}

			return (
				<InstalledTemplateItem
					key={item.id}
					template={item as unknown as InstalledCoMarketingTemplateWithCampaign}
				/>
			);
		});
	}, [displayedItems]);

	if (!needsAttentionLoaded || !combinedItems.length) {
		return null;
	}

	return (
		<>
			<Flex
				column
				gap="16"
			>
				<Flex
					align="center"
					justify="space-between"
				>
					<Typography variant="h2">Needs Attention</Typography>
					{combinedItems.length > cardLimit && (
						<Flex
							gap="4"
							align="center"
							style={{ cursor: 'pointer' }}
							onClick={() => {
								maximize(() => <NeedsAttentionPage />, { maximizedKey: 'needs-attention-co-marketing' });
							}}
						>
							<Typography
								variant="body"
								style={{ color: 'var(--brand-primary)', fontWeight: 700 }}
							>
								View All
							</Typography>
							<Icon
								height="16"
								width="16"
								type="CHEVRON_RIGHT"
								color="var(--brand-primary)"
							/>
						</Flex>
					)}
				</Flex>

				<div
					ref={containerRef}
					style={{ width: '100%' }}
				>
					<Flex
						gap="16"
						style={{
							overflowX: 'auto',
							overflowY: 'hidden',
							width: '100%',
							paddingBottom: '2px', // Small padding to prevent content cut-off
							// Hide scrollbar for webkit browsers (Chrome, Safari, Edge)
							WebkitOverflowScrolling: 'touch',
							scrollbarWidth: 'none', // Firefox
							msOverflowStyle: 'none', // IE and Edge
						}}
						css={`
							/* Hide scrollbar for webkit browsers */
							&::-webkit-scrollbar {
								display: none;
							}
						`}
					>
						{renderedItems}
					</Flex>
				</div>
			</Flex>
		</>
	);
}

export function CoMarketingNeedsAttentionNotificationIcon({ tab }: { tab: CampaignTabType }) {
	const { loaded: needsAttentionLoaded, combinedItems, totalSponsoredTemplates, initialize } = useNeedsAttentionStore();

	React.useEffect(() => {
		if (!needsAttentionLoaded) {
			initialize();
		}
	}, []);

	const shownLength = React.useMemo(() => {
		if (tab === 'sponsored') {
			return totalSponsoredTemplates;
		}
		return combinedItems.length;
	}, [tab, combinedItems, totalSponsoredTemplates]);

	if (shownLength <= 0) {
		return null;
	}

	return (
		<Flex
			css={`
				border-radius: 20px;
				background: var(--surface-brand, #6262f5);
				display: flex;
				padding: 1px 8px 2px 8px;
				align-items: center;
				gap: 8px;
				color: var(--text-primary-invert, #fff) !important;
				text-align: center;
				font-family: Manrope;
				font-size: 12px;
				font-style: normal;
				font-weight: 700;
				line-height: 18px; /* 150% */
				letter-spacing: 1px;
				text-transform: uppercase;
			`}
		>
			{formatNumber(shownLength)}
		</Flex>
	);
}
